import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { EASE_OUT } from '../../lib/motion';
import TierPricingCards from './tiers/TierPricingCards';
import TierFeatureMatrix from './tiers/TierFeatureMatrix';
import { FEATURE_MATRIX, TIER_CARDS, TIER_ORDER, type TierId } from './tiers/tierCatalog';
import ConfirmDialog from '../common/ConfirmDialog';
import SubscribeModal, { type SubscribeIntent } from './billing/SubscribeModal';
import { useAuth } from '../../contexts/AuthContext';
import { PERM } from '../../auth/permissions';
import {
  useUsage,
  useSubscription,
  useCatalog,
  useCancelSubscription,
  useReconciliation,
} from '../../services/billing';
import ReconciliationChooser from './billing/ReconciliationChooser';
import PendingCheckoutNotice from './billing/PendingCheckoutNotice';
import type { PlanId } from '../../types/billing';
import { phantomProps } from '@/config/phantom';
import { useTheme } from '@/contexts/ThemeContext';

/** What a workspace gives up moving down from `from` to `to` — computed from
 *  the real feature matrix so the copy can never drift from the offer. */
function lostBetween(from: TierId, to: TierId): string[] {
  const fmt = (v: string | boolean): string => (v === true ? 'included' : String(v));
  const lost: string[] = [];
  for (const group of FEATURE_MATRIX) {
    for (const row of group.rows) {
      const a = row.values[from];
      const b = row.values[to];
      if (a === b || a === false) continue;
      lost.push(b === false ? row.label : `${row.label} → ${fmt(b)} (now ${fmt(a)})`);
    }
  }
  return lost;
}

/** Sentence-ready list: first few concrete losses, "+ n more" for the rest. */
function lostSummary(from: TierId, to: TierId, max = 5): string {
  const lost = lostBetween(from, to);
  if (lost.length === 0) return '';
  const shown = lost.slice(0, max).join(' · ');
  const rest = lost.length - max;
  return rest > 0 ? `${shown} · and ${rest} more` : shown;
}

type TierDialog =
  | { kind: 'no-perm'; tierName: string }
  | { kind: 'current' }
  | { kind: 'enterprise' }
  | { kind: 'downgrade'; tier: TierId; tierName: string }
  | { kind: 'to-free' };

export default function TierManagement() {
  const { theme } = useTheme();
  const { activeMembership } = useAuth();

  const { data: usage, isLoading: usageLoading } = useUsage();
  const { data: subscription, isLoading: subLoading } = useSubscription();
  const { data: catalog = [] } = useCatalog();
  const cancelMutation = useCancelSubscription();
  const { data: reconciliation } = useReconciliation();

  const [dialog, setDialog] = useState<TierDialog | null>(null);
  const [modalIntent, setModalIntent] = useState<SubscribeIntent | null>(null);
  const [pendingPlan, setPendingPlan] = useState<TierId | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Reference material stays shut until asked for (Aleix, 2026-09-11).
  const [matrixOpen, setMatrixOpen] = useState(false);

  const isLoading = usageLoading || subLoading;

  // Billing permission gates plan changes — Owners and Admins hold it
  // (Admin = deputy Owner incl. billing, product decision 2026-07-14).
  const canManageBilling = activeMembership?.perms?.includes(PERM.BILLING_MANAGE) ?? false;

  const currentPlan: PlanId = subscription?.plan_id ?? 'free';
  const hasActiveSub = Boolean(subscription?.subscription_id) && currentPlan !== 'free';
  const pendingCheckout = subscription?.pending_checkout ?? null;
  const tierName = (id: TierId): string => TIER_CARDS.find((t) => t.id === id)?.name ?? id;

  const seatsUsed = usage?.seats_used ?? 0;
  const seatsPurchased = usage?.seats_purchased ?? 0;
  const seatPct = seatsPurchased > 0 ? Math.min((seatsUsed / seatsPurchased) * 100, 100) : 0;

  /** Open the subscribe/change-plan modal for a paid, self-serve plan. */
  const startPlanChange = (tierId: TierId) => {
    setError(null);
    setPendingPlan(tierId);
    setModalIntent(
      hasActiveSub ? { kind: 'change-plan', planId: tierId } : { kind: 'subscribe', planId: tierId }
    );
  };

  // Moving to Free = cancelling the subscription (Free is the
  // no-subscription state; access continues until the paid period ends).
  const handleCancelToFree = () => {
    setDialog(null);
    setError(null);
    cancelMutation.mutate(undefined, {
      onError: () => setError('Failed to cancel the subscription. Please try again.'),
    });
  };

  /** Reopen checkout for an unpaid one — same plan, same seats. */
  const completePendingCheckout = () => {
    if (!pendingCheckout) return;
    setError(null);
    setPendingPlan(pendingCheckout.plan_id);
    setModalIntent({
      kind: 'subscribe',
      planId: pendingCheckout.plan_id,
      seats: pendingCheckout.seats,
    });
  };

  /**
   * Product spec (2026-07-17): every tier card has an ACTIVE button — a click
   * either starts the change, or opens a dialog that says plainly what this
   * move means and what to do about it. No dead buttons, no silent no-ops.
   */
  const handleCardSelect = (tierId: TierId) => {
    const name = tierName(tierId);

    if (tierId === currentPlan) {
      setDialog({ kind: 'current' });
      return;
    }
    if (!canManageBilling) {
      setDialog({ kind: 'no-perm', tierName: name });
      return;
    }
    if (tierId === 'enterprise') {
      setDialog({ kind: 'enterprise' });
      return;
    }
    const isDowngrade = TIER_ORDER.indexOf(tierId) < TIER_ORDER.indexOf(currentPlan);
    if (isDowngrade) {
      setDialog(
        tierId === 'free'
          ? { kind: 'to-free' }
          : { kind: 'downgrade', tier: tierId, tierName: name }
      );
      return;
    }
    // Straight upgrade — open the in-app checkout.
    startPlanChange(tierId);
  };

  const closeModal = () => {
    setModalIntent(null);
    setPendingPlan(null);
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-text-primary mb-1">Plans</h2>
      <p className="text-sm text-text-secondary mb-6">Compare plans and manage your subscription</p>

      <phantom-ui {...phantomProps(theme === 'dark', isLoading)}>
        {/* Downgrade reconciliation: when open, the choice comes before
            everything else on this screen. */}
        {reconciliation?.open && <ReconciliationChooser state={reconciliation} />}

        {pendingCheckout && (
          <PendingCheckoutNotice
            planName={tierName(pendingCheckout.plan_id)}
            currentPlanName={tierName(currentPlan)}
            canManageBilling={canManageBilling}
            onComplete={completePendingCheckout}
          />
        )}

        {/* Seat usage */}
        {usage && (
          <div className="mb-8 bg-surface-primary rounded-lg p-6 border border-border-primary">
            <h3 className="text-base font-semibold text-text-primary mb-4">Current Usage</h3>

            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-text-secondary">Seats</span>
                <span className="text-sm text-text-tertiary">
                  {seatsUsed} / {seatsPurchased} used
                </span>
              </div>
              <div className="w-full bg-grey-200 dark:bg-grey-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-[width,background-color] duration-200 ${
                    seatPct > 80 ? 'bg-secondary-500' : 'bg-primary-500'
                  }`}
                  style={{ width: `${seatPct}%` }}
                />
              </div>
              {usage.seats_remaining <= 0 && (
                <p className="mt-2 text-xs text-secondary-600 dark:text-secondary-400">
                  All seats are in use. Add seats to invite more people.
                </p>
              )}
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-text-secondary">Active courses</span>
              <span className="text-sm text-text-tertiary">{usage.active_courses}</span>
            </div>
          </div>
        )}

        {/* The v3 offer: pricing cards + full feature matrix */}
        <div className="space-y-8">
          {/* Anchor target for the reconciliation banner/chooser upgrade CTAs. */}
          <div id="tier-plans" className="scroll-mt-24">
            <h3 className="text-base font-semibold text-text-primary mb-1">Available Plans</h3>
            <p className="text-sm text-text-secondary mb-4">
              A seat is a seat — trainer or learner, same price, switch anytime. Early customers
              keep launch prices forever.
            </p>
            <TierPricingCards
              currentTier={currentPlan}
              upgradingTier={pendingPlan}
              onSelect={handleCardSelect}
            />
          </div>

          {/* The full matrix is reference material, not the decision — it
              opens on request instead of pushing the cards off the screen
              (Aleix, 2026-09-11). */}
          <div>
            <button
              type="button"
              onClick={() => setMatrixOpen((open) => !open)}
              aria-expanded={matrixOpen}
              aria-controls="tier-feature-matrix"
              className="flex w-full items-center justify-between gap-3 rounded-xl border border-border-primary/20 px-4 py-3 text-left transition-colors hover:bg-text-primary/[0.03] focus-ring"
            >
              <span>
                <span className="block text-base font-semibold text-text-primary">
                  Compare everything
                </span>
                <span className="block text-sm text-text-secondary">
                  Every feature, plan by plan
                </span>
              </span>
              <ChevronDownIcon
                className={`h-5 w-5 shrink-0 text-text-secondary transition-transform duration-200 ${
                  matrixOpen ? 'rotate-180' : ''
                }`}
                aria-hidden="true"
              />
            </button>

            <AnimatePresence initial={false}>
              {matrixOpen && (
                <motion.div
                  id="tier-feature-matrix"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: EASE_OUT }}
                  className="overflow-hidden"
                >
                  <div className="pt-4">
                    <TierFeatureMatrix currentTier={currentPlan} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Billing-access notice */}
        {!canManageBilling && (
          <div className="mt-6 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-4">
            <p className="text-sm text-warning-800 dark:text-warning-200">
              <span className="font-semibold">Note:</span> Changing the subscription plan needs
              billing access. Ask an Owner or Admin.
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-6 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg p-4">
            <p className="text-sm text-error-800 dark:text-error-200">{error}</p>
          </div>
        )}

        {/* Card-click dialogs — every tier button answers "what happens?" */}
        {dialog?.kind === 'current' && (
          <ConfirmDialog
            title="This is your current plan"
            message="Your workspace is already on this plan. Pick any other card to move — each button tells you exactly what the change means before anything happens."
            confirmLabel="Got it"
            cancelLabel="Close"
            onConfirm={() => setDialog(null)}
            onCancel={() => setDialog(null)}
          />
        )}
        {dialog?.kind === 'no-perm' && (
          <ConfirmDialog
            title="Billing access needed"
            message={`Moving this workspace to ${dialog.tierName} needs billing access, which your account doesn't have. Ask an Admin — they can make the change right from this page.`}
            confirmLabel="Got it"
            cancelLabel="Close"
            onConfirm={() => setDialog(null)}
            onCancel={() => setDialog(null)}
          />
        )}
        {dialog?.kind === 'enterprise' && (
          <ConfirmDialog
            title="Enterprise — let's talk"
            message="Enterprise is tailored per organisation: pricing, user limits, integrations and support are agreed together. Send us an email and we'll get back to you within one business day."
            confirmLabel="Email contact@tereiko.com"
            cancelLabel="Close"
            onConfirm={() => {
              window.location.href =
                'mailto:contact@tereiko.com?subject=Enterprise%20plan%20enquiry';
              setDialog(null);
            }}
            onCancel={() => setDialog(null)}
          />
        )}
        {dialog?.kind === 'downgrade' && (
          <ConfirmDialog
            title={`Downgrade to ${dialog.tierName}?`}
            message={`You can switch now — the new price applies from your next billing period. Your workspace would give up: ${lostSummary(currentPlan, dialog.tier)}. Nothing is deleted — within 30 days you choose which members and published content stay within the new limits; until then everything keeps working, and members return automatically if you upgrade again.`}
            confirmLabel={`Downgrade to ${dialog.tierName}`}
            confirmVariant="danger"
            cancelLabel="Keep my plan"
            onConfirm={() => {
              const target = dialog.tier;
              setDialog(null);
              startPlanChange(target);
            }}
            onCancel={() => setDialog(null)}
          />
        )}
        {dialog?.kind === 'to-free' && (
          <ConfirmDialog
            title="Move to Free?"
            message={`Free is the plan without a subscription — moving here cancels your current one. You keep full access until the end of the period you've paid for. Your workspace would give up: ${lostSummary(currentPlan, 'free')}. Nothing is deleted — within 30 days you choose which members and published content stay within the new limits; until then everything keeps working, and members return automatically if you upgrade again.`}
            confirmLabel="Cancel my subscription"
            confirmVariant="danger"
            cancelLabel="Keep my plan"
            onConfirm={handleCancelToFree}
            onCancel={() => setDialog(null)}
          />
        )}
      </phantom-ui>

      {modalIntent && (
        <SubscribeModal
          isOpen={modalIntent !== null}
          onClose={closeModal}
          intent={modalIntent}
          subscription={subscription ?? null}
          usage={usage ?? null}
          catalog={catalog}
          onSuccess={closeModal}
        />
      )}
    </div>
  );
}
